import _ from "lodash";
import React, { useContext, useState, useMemo, useCallback, useEffect, useRef, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { initialSetup } from "../../implementationGlobals";
import { GameComponentForGame, GameState, SetVariableData, EventManagerForGame } from "../../implementationTypes";
import { PlayerEvent } from "src/components/types";
import { ComponentComp } from "../GameComponent";
import { PlayerArea } from "../GameComponentElements";
import { DeckContentPanel } from "./DeckContentPanel";
import { gameSituationContext } from "../GameSituationContext";
import { TableContext, TableContextType } from "../tableContext";
import { importAll } from "../tableUtility";
import { isModKeyDown } from "src/components/utils";

type GameSituationContainerProps = {
  statusBarStatus: string;
  outOfSync?: boolean;
  invitePwd?: string;
  addGameIconsToText: (text: string, options?: { [key: string]: any }) => ReactNode;
  moveToEvent?: (eventNumber: number) => void;
  currEventNumber?: number;
  toggleLog?: () => void;
  openChat?: () => void;
  setImagesLoaded?: (newValue: boolean) => void;
  increaseScaleAdjust?: (increaseBy: number) => void;
  decreaseScaleAdjust?: (decreaseBy: number) => void;
  changeZoomLensScale?: (delta: number) => void;
  displayTooltips: boolean;
  isZoomLens?: number;
  images: {
    [key: string]: string | { default: string };
  } | null;
};

export const GameSituationContainer = React.memo(
  ({
    statusBarStatus,
    outOfSync,
    invitePwd,
    addGameIconsToText,
    moveToEvent,
    currEventNumber,
    toggleLog,
    openChat,
    setImagesLoaded,
    increaseScaleAdjust,
    decreaseScaleAdjust,
    changeZoomLensScale,
    displayTooltips,
    isZoomLens,
    images,
  }: GameSituationContainerProps) => {
    const keysScaleAdjust = 3;
    const wheelScaleAdjust = 3;
    let {
      eventManager,
      evalClick,
      tableState,
      workingTableStateRef,
      getComponentInfo,
      adjustedGameState,
      playerColors,
      selectedColor,
      playAsColor,
      makeTurnsAs,
      evalAutoStates,
      reloadComponents,
      setEditMode,
      situationScale,
      editMode,
      defaultSelected,
      selectedComponent,
      setUserSelectedComponent,
      mousePos,
      toggleZoomLens,
      isClickable,
      openDeckContentId,
      deckContentSlotRef,
    } = useContext(TableContext as React.Context<TableContextType>);

    let params = useParams();
    let activeState = useMemo<GameState>(
      () => {
        if (!tableState) {
          return {};
        }
        return adjustedGameState(tableState?.getActiveState());
      },
      [tableState?.getActiveState(), playAsColor, selectedColor], // really not sure whether this is good practice, but it works
    );

    let [bankReveal, setBankReveal] = useState<GameComponentForGame | null>(null);

    let handleKeyPress = useCallback(
      function (evt: KeyboardEvent) {
        if (isZoomLens || !moveToEvent || currEventNumber === undefined) {
          return;
        }
        if (document.activeElement?.role === "textbox" || document.activeElement?.localName === "textarea") {
          return true;
        }
        let moveAmt = isModKeyDown(evt) ? 10 : 1;
        switch (evt.key) {
          case "Delete":
            eventManager.newEvent("game move", {
              sourcePlayer: makeTurnsAs,
              componentData: selectedComponent,
              destination: selectedComponent?.origin,
              type: "discard",
            } as PlayerEvent);
            setUserSelectedComponent(null);
            break;
          case "f":
            eventManager.newEvent("game move", {
              sourcePlayer: makeTurnsAs,
              componentId: selectedComponent?.id,
              optionName: "cardFlipped",
              newValue: !selectedComponent?.options.cardFlipped,
              type: "set component option",
            } as PlayerEvent);
            setUserSelectedComponent(null);
            break;
          case "g":
            console.log("pinging", mousePos?.current);
            eventManager.newEvent("ping", {
              ...(mousePos?.current ?? {}),
              type: "ping",
              sourcePlayer: makeTurnsAs ?? "",
            });
            break;
          case "Escape":
            setUserSelectedComponent(null);
            setBankReveal(null);
            break;
          case "e":
            setEditMode(!editMode);
            break;
          case "r":
            reloadComponents();
            break;
          case "ArrowLeft":
            moveToEvent(currEventNumber - moveAmt);
            evt.preventDefault();
            break;
          case "ArrowRight":
            moveToEvent(currEventNumber + moveAmt);
            evt.preventDefault();
            break;
          case "Alt":
          case "z":
            toggleZoomLens(true);
            evt.preventDefault();
            break;
          case "l":
            if (toggleLog) {
              toggleLog();
            }
            break;
          case "Enter":
            if (openChat) {
              openChat();
            }
            evt.preventDefault();
            break;
          case "+":
            if (isModKeyDown(evt) && increaseScaleAdjust) {
              evt.preventDefault();
              increaseScaleAdjust(keysScaleAdjust);
            }
            break;
          case "-":
            if (isModKeyDown(evt) && decreaseScaleAdjust) {
              evt.preventDefault();
              decreaseScaleAdjust(keysScaleAdjust);
            }
            break;
          default:
            break;
        }
      },
      [
        isZoomLens,
        moveToEvent,
        currEventNumber,
        document,
        eventManager,
        makeTurnsAs,
        selectedComponent,
        selectedComponent?.options.cardFlipped,
        setUserSelectedComponent,
        setBankReveal,
        toggleZoomLens,
        toggleLog,
        openChat,
        reloadComponents,
        setEditMode,
        increaseScaleAdjust,
        decreaseScaleAdjust,
        mousePos,
      ],
    );

    let handleKeyUp = useCallback(
      (evt: KeyboardEvent) => {
        switch (evt.key) {
          case "Alt":
          case "z":
            toggleZoomLens(false);
            evt.preventDefault();
            break;
        }
      },
      [toggleZoomLens],
    );

    let handleMouseScroll = useCallback(
      function (evt: WheelEvent) {
        if (evt.ctrlKey && evt.deltaY < 0 && increaseScaleAdjust) {
          evt.preventDefault();
          increaseScaleAdjust(wheelScaleAdjust * situationScale * 3);
        } else if (evt.ctrlKey && evt.deltaY > 0 && decreaseScaleAdjust) {
          evt.preventDefault();
          decreaseScaleAdjust(wheelScaleAdjust * situationScale * 3);
        } else if (evt.altKey && changeZoomLensScale) {
          changeZoomLensScale(evt.deltaY);
          evt.preventDefault(); // prevent alt+wheel navigation because alt is for zoom lens
        }
      },
      [increaseScaleAdjust, decreaseScaleAdjust, changeZoomLensScale],
    );

    useEffect(() => {
      window.addEventListener("keydown", handleKeyPress);
      window.addEventListener("keyup", handleKeyUp);
      window.addEventListener("wheel", handleMouseScroll, { capture: true, passive: false });
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
        window.removeEventListener("keyup", handleKeyUp);
        window.removeEventListener("wheel", handleMouseScroll, { capture: true });
      };
    }, [handleKeyPress, handleKeyUp, handleMouseScroll]);

    let rootComponent = null;
    let baseWidth = 0;
    let baseHeight = 0;
    let playerAreas = [];

    let tooltipWrapRef = useRef<HTMLDivElement | null>(null);
    const macroClickCallBack = useCallback(
      (name: string, options: { [key: string]: any }, buttonData: { [key: string]: any }) => {
        console.log("clicked macro", name, options);
        eventManager.newEvent("game move", {
          sourcePlayer: makeTurnsAs,
          componentId: options.componentId,
          options,
          buttonName: name,
          onlyEditMode: buttonData.onlyEditMode,
          type: "press button",
        } as PlayerEvent);
      },
      [eventManager, makeTurnsAs],
    );

    const optionSelectCallBack = useCallback(
      (options: SetVariableData, evt: React.ChangeEvent) => {
        let target = evt.target as HTMLInputElement;
        console.log("clicked option", options, target.value);
        eventManager.newEvent("game move", {
          sourcePlayer: makeTurnsAs,
          componentId: options.componentId,
          optionName: options.optionName,
          newValue: target.value,
          type: "set component option",
        } as PlayerEvent);
      },
      [eventManager, makeTurnsAs],
    );

    const discardComponentCallBack = useCallback(
      (componentData: GameComponentForGame, destination: string) => {
        console.log("discarding", componentData, "to", destination);
        eventManager.newEvent("game move", {
          sourcePlayer: makeTurnsAs,
          componentData: componentData,
          destination: destination,
          type: "discard",
        } as PlayerEvent);
        setUserSelectedComponent(null);
      },
      [eventManager, makeTurnsAs],
    );

    const deckClicked = useCallback(
      (deckData: GameComponentForGame) => {
        console.log("clicked deck", deckData);
        if (selectedComponent && selectedComponent.type !== "counter") {
          eventManager.newEvent("game move", {
            type: "return to top",
            sourcePlayer: makeTurnsAs,
            componentData: selectedComponent,
            destination: deckData.id,
          } as PlayerEvent);
          setUserSelectedComponent(null);
          return;
        }
        if (bankReveal === deckData) {
          setBankReveal(null);
        } else {
          setBankReveal(deckData);
        }
      },
      [eventManager, makeTurnsAs, bankReveal, selectedComponent],
    );

    const bankClicked = useCallback(
      (bankData: GameComponentForGame) => {
        console.log("clicked bank", bankData);
        if (selectedComponent && selectedComponent.type !== "counter") {
          eventManager.newEvent("game move", {
            type: "return to top",
            sourcePlayer: makeTurnsAs,
            componentData: selectedComponent,
            destination: bankData.id,
          } as PlayerEvent);
          return;
        }
        if (bankReveal === bankData) {
          setBankReveal(null);
        } else {
          setBankReveal(bankData);
        }
      },
      [eventManager, makeTurnsAs, bankReveal, selectedComponent],
    );

    const moveToBank = useCallback(
      (bankData: GameComponentForGame) => {
        eventManager.newEvent("game move", {
          sourcePlayer: makeTurnsAs,
          componentId: selectedComponent?.id, // just backwards compatibility
          componentData: selectedComponent,
          containerId: bankData.id,
          type: "move to bank",
        } as PlayerEvent);
        setUserSelectedComponent(null);
      },
      [eventManager, makeTurnsAs, selectedComponent],
    );

    const positionClickCallBack = useCallback(
      (
        destinationId: string,
        positionIndex: string | number,
        positionName: string,
        visible: any,
        evt: React.MouseEvent,
      ) => {
        console.log("position clicked", positionName, positionIndex);
        if (statusBarStatus !== "waitUserInput" && !editMode) {
          eventManager.createAlert("Please wait, an action is already in progress", "pink");
          return;
        }
        if (bankReveal) {
          console.log("sending move to reveal from deck", bankReveal.id);
          setBankReveal(null);
          eventManager.newEvent("game move", {
            sourcePlayer: makeTurnsAs,
            destinationId,
            positionName,
            positionIndex,
            type: "component reveal",
            containerId: bankReveal.id,
            forcePickId: selectedComponent?.id,
          } as PlayerEvent);
        } else {
          eventManager.newEvent("game move", {
            sourcePlayer: makeTurnsAs,
            componentId: selectedComponent?.id,
            componentData: selectedComponent,
            destinationId,
            positionName,
            positionIndex,
            posVisible: visible,
            type: "component move",
          } as PlayerEvent);
        }
        setUserSelectedComponent(null);
        evt.stopPropagation();
      },
      [eventManager, bankReveal, selectedComponent, makeTurnsAs, statusBarStatus, editMode],
    );

    const componentClickCallBack = useCallback(
      (componentData: GameComponentForGame, evt: React.MouseEvent) => {
        console.log("clicked", evt.target, componentData);
        let target = evt.target as HTMLElement;
        if (outOfSync) {
          eventManager.createAlert("You cannot do turns when out of sync. Please, Try to reload the page.", "pink");
          return;
        }
        if (statusBarStatus !== "waitUserInput" && !editMode) {
          eventManager.createAlert("Please wait, an action is already in progress", "pink");
          return;
        }
        setBankReveal(null);
        if (getComponentInfo(componentData)?.selectable === false && !editMode) {
          return;
        }
        if (editMode && getComponentInfo(componentData)?.editModeSelectable === false) {
          return;
        }
        if (componentData.name === "player-area") {
          return;
        }
        function defaultClick() {
          if (target.classList.contains("selected")) {
            setUserSelectedComponent(null);
          } else if (componentData.type === "deck") {
            deckClicked(componentData);
          } else if (componentData.type === "bank") {
            if (selectedComponent && componentData.options.contentNames.includes(selectedComponent?.name)) {
              moveToBank(componentData);
            } else {
              bankClicked(componentData);
            }
          } else {
            const target = evt.target as HTMLElement | null;
            const deckDisplay = target?.closest(".deck-content-panel") as HTMLElement | null;
            if (deckDisplay) {
              setBankReveal(tempTableState.getGameComponentById(deckDisplay.dataset.id) ?? null);
            }
            setUserSelectedComponent(componentData);
          }
        }
        eventManager.newClick();
        evt.stopPropagation();
        let tempTableState = (workingTableStateRef.current = _.cloneDeep(tableState));
        try {
          initialSetup(tempTableState, eventManager as EventManagerForGame, makeTurnsAs);
          if (outOfSync) {
            throw new Error("You cannot do more moves when out of sync. Please, refresh.");
          }
          if (editMode) {
            defaultClick();
          } else {
            let stateEvalResult;
            let rectElement = evt.currentTarget;
            if (rectElement.children[0]) {
              rectElement = rectElement.children[0] as HTMLElement;
            }
            let rect = rectElement.getBoundingClientRect();
            let componentPos = {
              x:
                ((evt.clientX - rect.left) / rect.width) *
                (getComponentInfo(componentData)?.positions?.dims?.[0] ?? 100),
              y:
                ((evt.clientY - rect.top) / rect.height) *
                (getComponentInfo(componentData)?.positions?.dims?.[1] ?? 100),
            };
            if (activeState.behavior?.evalClick) {
              stateEvalResult = activeState.behavior.evalClick(
                componentData,
                makeTurnsAs,
                isClickable(componentData, tempTableState),
                componentPos,
              );
              //stateEvalResult = activeState.behavior.evalClick(componentData, tempTableState, eventManager, makeTurnsAs);
              console.log("state evalClick result", stateEvalResult);
            }
            if (stateEvalResult === "select") {
              setUserSelectedComponent(componentData);
            } else {
              if (stateEvalResult) {
                evalAutoStates(tempTableState, eventManager, makeTurnsAs);

                eventManager.finalizeClick(
                  componentData,
                  makeTurnsAs ?? "",
                  tempTableState.getActiveState().activePlayer,
                  activeState.behavior.evalClick,
                );
              } else {
                let evalResult = evalClick(
                  componentData,
                  makeTurnsAs,
                  isClickable(componentData, tempTableState),
                  componentPos,
                );
                console.log("evalClick result", evalResult);
                if (evalResult) {
                  evalAutoStates(tempTableState, eventManager, makeTurnsAs);
                  eventManager.finalizeClick(
                    componentData,
                    makeTurnsAs ?? "",
                    tempTableState.getActiveState().activePlayer,
                    evalClick as () => any,
                  );
                  if (evalResult === "select") {
                    setUserSelectedComponent(componentData);
                  } else {
                    setUserSelectedComponent(null);
                  }
                } else {
                  defaultClick();
                }
              }
            }
          }
        } catch (errMessage) {
          if (!eventManager.showMovePrevent(false)) {
            eventManager.createAlert(("SCRIPT ERROR: " + errMessage) as string, "blue");
            console.error(errMessage);
            return;
          }
        }
        evt.stopPropagation();
      },
      [
        eventManager,
        activeState,
        bankClicked,
        deckClicked,
        editMode,
        evalAutoStates,
        evalClick,
        getComponentInfo,
        selectedComponent,
        moveToBank,
        makeTurnsAs,
        playAsColor,
        statusBarStatus,
        tableState,
        workingTableStateRef,
      ],
    );

    const situationContextValue = {
      componentClickCallBack,
      positionClickCallBack,
      discardComponentCallBack,
      optionSelectCallBack,
      macroClickCallBack,
      bankReveal,
      addGameIconsToText,
      displayTooltips,
      selectedComponent,
    };

    if (tableState?.root) {
      rootComponent = (
        <gameSituationContext.Provider value={situationContextValue}>
          <ComponentComp
            data={tableState.root}
            tooltipWrapRef={tooltipWrapRef as React.MutableRefObject<HTMLElement>}
            situationScale={situationScale}
            key={tableState.root.id}
          />
        </gameSituationContext.Provider>
      );
      let rootInfo = getComponentInfo(tableState.root as GameComponentForGame);
      baseWidth = rootInfo.width;
      baseHeight = rootInfo.height;

      for (let player of tableState.players) {
        let areaInfo = getComponentInfo(player.area);
        let areaComponent = (
          <gameSituationContext.Provider value={situationContextValue} key={"player-area-" + player.color}>
            <PlayerArea
              width={areaInfo.width || 0}
              height={areaInfo.height || 0}
              x={areaInfo.x || 0}
              y={areaInfo.y || 0}
              counters={player.counters}
              selected={selectedColor}
              //data={player}
              color={player.color}
              userInfo={
                playerColors?.filter((p) => p.color === player.color)[0]?.userInfo ?? {
                  username: "Dummy-" + player.color,
                }
              }
              allPlayers={tableState.players}
              active={activeState.activePlayer === player.color}
              //endTurn={endTurn}
              invitePwd={invitePwd}
              customOrder={player.customAttrs.customOrder}
            >
              <ComponentComp
                data={player.area}
                x={50}
                y={50}
                tooltipWrapRef={tooltipWrapRef}
                playerColor={player.color}
              ></ComponentComp>
            </PlayerArea>
          </gameSituationContext.Provider>
        );
        playerAreas.push(areaComponent);
      }
    }

    if (!images) {
      return <div className="project-main">Loading images...</div>;
    }
    let className = "game-situation";
    if (selectedComponent || bankReveal) {
      className += " select-position";
    }
    if (isZoomLens) {
      className += " zoom-lens";
    }
    let areaInfo = getComponentInfo(tableState?.players[0].area);
    let minMult = [1, 1, 1.6, 1.6, 2.2][tableState?.players.length];
    let height = Math.max(areaInfo?.height * minMult + areaInfo?.y + 20, baseHeight);
    let width = Math.max(areaInfo?.width + areaInfo?.x + 20, baseWidth);
    let style: { [key: string]: any } = {
      width,
      height,
      backgroundImage: "url(/assets/wood_bg.jpg)",
      backgroundSize: "contain",
      transition: "background-image .2s",
    };
    if (editMode) {
      style.backgroundImage = "url(/assets/blueprint.jpg)";
    }

    if (situationScale) {
      let globalTargetScale = situationScale * (isZoomLens ?? 1);
      style.transform = "scale(" + globalTargetScale + ")";

      style.marginBottom = style.height * globalTargetScale - style.height;
      style.marginRight = style.width * globalTargetScale - style.width;
      style.transformOrigin = "top left";
    }
    return (
      <>
        <div
          className={className}
          style={style}
          tabIndex={0}
          //https://stackoverflow.com/questions/43503964/onkeydown-event-not-working-on-divs-in-react
        >
          <div className="flyer-wrap"></div>
          <div className="tooltip-wrap" ref={tooltipWrapRef}></div>

          {rootComponent}
          {playerAreas}
        </div>
        {!isZoomLens && openDeckContentId && deckContentSlotRef?.current
          ? createPortal(
              <gameSituationContext.Provider value={situationContextValue}>
                <DeckContentPanel key={openDeckContentId} containerId={openDeckContentId} />
              </gameSituationContext.Provider>,
              deckContentSlotRef.current,
            )
          : null}
      </>
    );
  },
);
